import { api } from '../../../services/api/client';
import type { Order, Payment } from '../../../types';

export interface DuitkuMethod {
  paymentMethod: string;
  paymentName: string;
  paymentImage?: string;
  totalFee: string;
}

export interface PaymentMethodsResult {
  mode: string;
  methods: DuitkuMethod[];
  message: string;
}

export interface CreateOrderPayload {
  product_type: string;
  product_id: string;
}

export interface PayResult {
  order: Order;
  payment: Payment;
  checkout_url: string;
}

export async function createOrder(payload: CreateOrderPayload): Promise<Order> {
  const { data } = await api.post<Order>('/member/orders', payload);
  return data;
}

export async function listOrders(): Promise<Order[]> {
  const { data } = await api.get<Order[]>('/member/orders');
  return data;
}

export async function listPayments(): Promise<Payment[]> {
  const { data } = await api.get<Payment[]>('/member/payments');
  return data;
}

export async function payOrder(orderId: string, paymentMethod?: string): Promise<PayResult> {
  const { data } = await api.post<PayResult>(`/member/orders/${orderId}/pay`, paymentMethod ? { payment_method: paymentMethod } : {});
  return data;
}

export async function fetchPaymentMethods(amount: number): Promise<PaymentMethodsResult> {
  const { data } = await api.get<PaymentMethodsResult>('/member/payments/methods', { params: { amount } });
  return data;
}

export async function checkOrderStatus(orderId: string): Promise<Order> {
  const { data } = await api.get<Order>(`/member/orders/${orderId}/status`);
  return data;
}

export interface OrderDetail {
  order: Order;
  payment: Payment | null;
}
export async function getOrderDetail(orderId: string): Promise<OrderDetail> {
  const { data } = await api.get<OrderDetail>(`/member/orders/${orderId}/detail`);
  return data;
}
export async function retryOrder(orderId: string): Promise<Order> {
  const { data } = await api.post<Order>(`/member/orders/${orderId}/retry`);
  return data;
}

export interface SalesAnalytics {
  total_orders: number;
  paid: number;
  pending: number;
  failed: number;
  expired: number;
  revenue: number;
  currency: string;
  conversion_rate: number;
}
export interface SalesTrendPoint { date: string; revenue: number; }
export async function adminSalesTrend(period: 'daily' | 'monthly'): Promise<{ period: string; points: SalesTrendPoint[] }> {
  const { data } = await api.get('/admin/analytics/sales-trend', { params: { period } });
  return data;
}
export async function downloadInvoice(orderId: string): Promise<Blob> {
  const { data } = await api.get(`/member/orders/${orderId}/invoice`, { responseType: 'blob' });
  return data;
}

export async function adminSalesAnalytics(): Promise<SalesAnalytics> {
  const { data } = await api.get<SalesAnalytics>('/admin/analytics/sales');
  return data;
}

export async function adminListOrders(): Promise<Order[]> {
  const { data } = await api.get<Order[]>('/admin/orders');
  return data;
}

export async function adminListPayments(): Promise<Payment[]> {
  const { data } = await api.get<Payment[]>('/admin/payments');
  return data;
}

export interface WaTarget { order_id: string; name: string; phone: string; wa_link: string; }
export interface FollowUpResult {
  total: number;
  sent_email: number;
  skipped_no_email: number;
  skipped_no_phone: number;
  wa_targets: WaTarget[];
}
export interface FollowUpPayload {
  order_ids: string[];
  channel: 'email' | 'whatsapp' | 'both';
  subject?: string;
  message: string;
}
export async function adminFollowUp(payload: FollowUpPayload): Promise<FollowUpResult> {
  const { data } = await api.post<FollowUpResult>('/admin/orders/followup', payload);
  return data;
}
